/**
 * AppsPage — list of deployed managed-app instances for a project.
 *
 * Mounted at /projects/:projectId/apps as a child of ProjectLayout (so
 * the sidebar / header / project context come from the layout). Just
 * the list content lives here.
 *
 * Data: GET /apps?projectId=<id> returns the project's deployed app
 * rows newest-first. Click a row → /projects/:projectId/apps/:instanceId.
 *
 * Visual language is a deliberate clone of PipelinesPage:
 *   - same `pipelines-page` wrapper + `pipelines-header*` chrome
 *   - same shared DataTable atom with column class names from the
 *     pipelines stylesheet
 *   - same dark theme + no new colors
 * Deploying a new app goes through the marketplace, same as deploying
 * a new pipeline goes through the workflow marketplace.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DataTable from '../../components/shared/DataTable/DataTable';
import AppStatus from './AppStatus';
import AppsDemo from '../../components/shared/GetStartedSteps/AppsDemo';
import McpDemo from '../../components/shared/GetStartedSteps/McpDemo';
import DemoCarousel from '../../components/shared/GetStartedSteps/DemoCarousel';
import { Apps } from '../../services/apps';
import { timeAgo } from '../../utils/timeAgo';
// Reuse the pipelines stylesheet for header / wrapper / column widths so
// the two list pages look like siblings.
import { projectAppsMarketplacePath } from '../../utils/marketplacePaths';
import SoloDestroyModal from '../../components/shared/SoloDestroyModal/SoloDestroyModal';
import LetterAvatar from '../../components/atoms/LetterAvatar/LetterAvatar';
import '../PipelinesPage/PipelinesPage.css';

// App logo in the NAME cell — mirrors PipelinesPage's WorkflowIcon. The
// deployed-instance list API does NOT carry the catalog icon, so AppsPage
// fetches the (public) catalog once and maps appType → icon URL. On a
// missing/flaky icon we fall back to the shared LetterAvatar placeholder
// instead of a broken image.
const AppIcon = ({ iconUrl, label }) => {
  const [failed, setFailed] = useState(false);
  if (!iconUrl || failed) {
    return <LetterAvatar name={label} />;
  }
  return (
    <img
      src={iconUrl}
      onError={() => setFailed(true)}
      className="pipelines-name-icon"
      alt=""
      loading="lazy"
    />
  );
};

const AppsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  // appType → catalog icon URL, fetched once from the public catalog so
  // deployed rows can show the app's logo (the instance list omits it).
  const [iconByType, setIconByType] = useState({});
  // App delete confirmation: holds the app row pending a styled-modal
  // destroy (null = modal closed). Both solo (type-to-confirm, data loss)
  // and cloud (plain styled confirm) flow through the same modal — the row's
  // deployMode decides which variant renders. destroyBusy gates the in-flight
  // DELETE.
  const [destroyTarget, setDestroyTarget] = useState(null);
  const [destroyBusy, setDestroyBusy] = useState(false);

  // Toaster ref so re-renders don't bust fetchApps' identity (same
  // pattern PipelinesPage uses — see the comment there for context).
  const setToastRef = useRef(setToast);
  useEffect(() => { setToastRef.current = setToast; }, [setToast]);

  const fetchApps = useCallback(async (opts = {}) => {
    const { isRefetch = false } = opts;
    if (!isRefetch) setLoading(true);
    try {
      // Two sources, merged:
      //  1. Project-scoped cloud apps (Fargate). These carry a projectId
      //     and are indexed by ProjectCreatedAtIndex — the only rows the
      //     `?projectId=` query returns.
      //  2. Account-scoped solo apps (dedicated EC2). Solo rows have NO
      //     projectId, so they are absent from the GSI above — they only
      //     appear in the account-wide list (`GET /apps` with no param).
      //     We pull that list and keep ONLY deployMode==='solo' rows so we
      //     don't surface other projects' cloud apps on a project page.
      const [projResp, acctResp] = await Promise.all([
        Apps.listInstances({ projectId }),
        Apps.listInstances({}).catch((e) => {
          // Solo is additive — a failure here must not blank the cloud list.
          console.error('Failed to fetch solo apps:', e);
          return { apps: [] };
        }),
      ]);
      const cloud = Array.isArray(projResp?.apps) ? projResp.apps : [];
      // KEEP terminated solo apps in the list. A terminated VM is gone, but
      // its agent-opsd + bootstrap logs persist in CloudWatch (/zibby/solo/
      // <stage>, 14-day retention) — the row is retained for audit (status=
      // terminated, no destroyedAt) precisely so the user can still open the
      // detail page and read that history. Filtering them out here severed
      // the only path to those logs.
      const solo = (Array.isArray(acctResp?.apps) ? acctResp.apps : [])
        .filter((a) => a.deployMode === 'solo');
      setApps([...cloud, ...solo]);
    } catch (err) {
      console.error('Failed to fetch apps:', err);
      setToastRef.current?.({ isOpen: true, message: `Could not load apps: ${err.message}`, color: 'danger' });
      if (!isRefetch) setApps([]);
    } finally {
      if (!isRefetch) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // Catalog icons — load once. Non-fatal: on failure rows just fall back
  // to the LetterAvatar placeholder, so we don't surface an error.
  useEffect(() => {
    let alive = true;
    Apps.listCatalog()
      .then((data) => {
        if (!alive) return;
        const map = {};
        for (const c of (data?.items || [])) {
          if (c.appType) map[c.appType] = c.icon || null;
        }
        setIconByType(map);
      })
      .catch((e) => console.warn('apps catalog icons unavailable:', e?.message));
    return () => { alive = false; };
  }, []);

  // ── solo helpers ─────────────────────────────────────────────────────
  // Solo apps (deployMode==='solo') run on a dedicated EC2, are keyed by
  // appSlug (not instanceId), expose a public hostname (`domain`), and
  // MUST be torn down via DELETE /apps/solo/{slug} so the VM + Elastic IP
  // + Route53 record are all cleaned up. Cloud apps are unchanged.
  const isSolo = (a) => a?.deployMode === 'solo';
  // Stable row key: solo rows are keyed by slug (instanceId churns on
  // resume), cloud rows by instanceId.
  const keyFor = (a) => (isSolo(a) ? `solo:${a.appSlug}` : a.instanceId);
  // Public URL: solo prefers its trusted https domain, falling back to
  // the raw public IP (http, pre-DNS). Cloud keeps url/subdomain.
  const urlFor = (a) => {
    if (isSolo(a)) {
      if (a.domain) return `https://${a.domain}`;
      if (a.publicIp) return `http://${a.publicIp}`;
      return null;
    }
    return a.url || (a.subdomain ? `https://${a.subdomain}` : null);
  };
  // Solo status for the pill: prefer the lifecycle `status`
  // (active/paused/terminated), but while provisioning surface the
  // finer-grained `phase` (provisioning/running/failed).
  const statusFor = (a) => {
    if (isSolo(a)) {
      if (a.status === 'active') return a.phase || a.status;
      return a.status || a.phase;
    }
    return a.status;
  };

  // ── columns ────────────────────────────────────────────────────────
  // Mirror the Pipelines columns: NAME / STATUS / VERSION-equivalent /
  // LAST DEPLOYED. The app-specific "VERSION" column is the app's
  // catalog version (n8n v1.97.1 etc) since users care about that more
  // than the instance row's internal version number.
  const columns = [
    {
      header: 'NAME',
      key: 'name',
      className: 'pipelines-col-name',
      render: (a) => {
        const label = a.name || a.appType || a.appSlug || a.instanceId;
        return (
          <span className="pipelines-name-cell">
            <AppIcon iconUrl={iconByType[a.appType] || iconByType[a.appSlug]} label={label} />
            <span className="pipelines-name-text">
              <span className="pipelines-name">{label}</span>
            </span>
            {/* Only flag dedicated (solo) apps — "Cloud" is the default and
                the badge added no signal, just noise. */}
            {isSolo(a) && (
              <span className="apps-deploy-badge" data-mode="solo">Dedicated</span>
            )}
          </span>
        );
      },
    },
    {
      header: 'STATUS',
      key: 'status',
      className: 'pipelines-col-status',
      render: (a) => (
        <AppStatus
          status={statusFor(a)}
          runningCount={a.runningCount}
          desiredCount={a.desiredCount}
          variant="table"
        />
      ),
    },
    {
      header: 'APP',
      key: 'appType',
      className: 'pipelines-col-version',
      render: (a) => (
        <span className="pipelines-version">
          {isSolo(a)
            ? `${a.appSlug}${a.tier ? ` · ${a.tier}` : ''}`
            : `${a.appType || ''}${a.appVersion ? ` v${a.appVersion}` : ''}`}
        </span>
      ),
    },
    {
      header: 'CREATED',
      key: 'createdAt',
      className: 'pipelines-col-deployed',
      render: (a) => (
        <span className="pipelines-deployed">
          {a.createdAt ? timeAgo(a.createdAt) : '—'}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'View details',
      // Cloud apps have an instanceId-keyed detail page; solo apps have a
      // slug-keyed one (/apps/solo/:slug). Both live under the current
      // project route so the layout/sidebar context comes along.
      onClick: (a) => {
        if (isSolo(a)) {
          navigate(`/projects/${projectId}/apps/solo/${a.appSlug}`);
          return;
        }
        navigate(`/projects/${projectId}/apps/${a.instanceId}`);
      },
    },
    {
      label: 'Open app',
      onClick: (a) => {
        const url = urlFor(a);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      },
    },
    { divider: true },
    {
      label: 'Destroy',
      danger: true,
      // Both solo and cloud teardown open the styled DestroyAppModal rather
      // than a bare confirm(). The modal renders the type-to-confirm /
      // data-loss variant for solo (deployMode==='solo') and a plain styled
      // confirm for cloud — see the SoloDestroyModal render below. The
      // actual DELETE runs in confirmDestroy once the user confirms.
      onClick: (a) => { setDestroyTarget(a); },
    },
  ];

  const confirmDestroy = async () => {
    const a = destroyTarget;
    if (!a) return;
    const name = a.name || a.appSlug || a.instanceId;
    setDestroyBusy(true);
    try {
      if (isSolo(a)) {
        // SOLO teardown — DELETE /apps/solo/{slug}. Terminates the VM,
        // releases its Elastic IP, removes the Route53 record, AND deletes
        // the EBS volume + S3 (Litestream) backup. Irreversible data loss.
        await Apps.destroySolo(a.appSlug);
        // Keep the row (mark terminated in place) rather than dropping it —
        // the VM is gone but its CloudWatch logs persist (14d), and the row
        // stays the entry point to view that history. A full refetch would
        // surface it as terminated anyway; do it optimistically here.
        setApps((prev) => prev.map((x) => (keyFor(x) === keyFor(a)
          ? { ...x, status: 'terminated', phase: 'terminated' }
          : x)));
        setDestroyTarget(null);
        setToast?.({ isOpen: true, message: `App "${name}" deleted — VM + EBS + S3 removed; logs kept 14d`, color: 'success' });
      } else {
        // CLOUD teardown — DELETE /apps/{instanceId}. Stops the Fargate task
        // and removes the service. Less destructive than solo.
        await Apps.destroy(a.instanceId);
        setApps((prev) => prev.filter((x) => x.instanceId !== a.instanceId));
        setDestroyTarget(null);
        setToast?.({ isOpen: true, message: `App "${name}" destroyed`, color: 'success' });
      }
    } catch (err) {
      setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
    } finally {
      setDestroyBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Apps · Zibby</title>
      </Helmet>

      <SoloDestroyModal
        open={!!destroyTarget}
        requireType={!!destroyTarget && isSolo(destroyTarget)}
        label={destroyTarget
          ? (isSolo(destroyTarget)
            ? destroyTarget.appSlug
            : (destroyTarget.name || destroyTarget.appType || destroyTarget.instanceId))
          : ''}
        confirmWord={destroyTarget?.appSlug}
        busy={destroyBusy}
        onClose={() => setDestroyTarget(null)}
        onConfirm={confirmDestroy}
      />

      {/* Empty state matches Pipelines exactly — the AppsDemo terminal
          mockup sits in a .pipelines-empty wrapper, no outer page
          padding. WorkflowDemo / AppsDemo handle their own chrome. */}
      {!loading && apps.length === 0 ? (
        <div className="pipelines-empty">
          <DemoCarousel slides={[() => <McpDemo context="apps" />, () => <AppsDemo />]} />
        </div>
      ) : (
        <div className="pipelines-page">
          {!loading && apps.length > 0 && (
            <div className="pipelines-header">
              <div className="pipelines-header-row">
                <h1 className="pipelines-title">Apps</h1>
              </div>
              <p className="pipelines-subtitle">
                Managed app instances deployed in this project.
                {' '}Deploy a new one from the{' '}
                <Link to={projectAppsMarketplacePath(projectId)} className="pipelines-subtitle-link">
                  app marketplace
                </Link>.
              </p>
            </div>
          )}

          <DataTable
            data={apps}
            columns={columns}
            loading={loading}
            rowKey="instanceId"
            actions={actions}
            onRowClick={(a) => {
              // Solo rows open their slug-keyed detail page; cloud rows open
              // the instanceId-keyed one. Both stay under the project route.
              if (isSolo(a)) {
                navigate(`/projects/${projectId}/apps/solo/${a.appSlug}`);
                return;
              }
              navigate(`/projects/${projectId}/apps/${a.instanceId}`);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AppsPage;
